import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';

import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useDelayedState } from '@hooks/useDelayedState';
import { useMounted } from '@hooks/useMounted';
import { OverlayState } from '@stores/useOverlayStore';
import { Spacing } from 'chatie-design-system';
import { Close, Icon } from 'chatie-design-system/icon';

import { MAX_WIDTH, safeArea } from '@styles/css';
import { CSSProps } from '@utils/misc';

/** Store */

/** Component */
const overlayCSS = (open: boolean, delayOpen: boolean, depth = 0) => css`
  position: fixed;
  top: 0;
  max-width: ${MAX_WIDTH}px;
  width: 100%;
  height: 100%;
  z-index: ${99 + depth};

  ${open
    ? css`
        visibility: visible;
      `
    : css`
        visibility: hidden;
        transition: visibility 0s 0.4s;
      `}

  ${!delayOpen &&
  css`
    pointer-events: none;
  `}
`;

const bgCSS = (isOpen: boolean) => (theme: Theme) =>
  css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-width: ${MAX_WIDTH}px;
    height: 100%;
    background-color: ${theme.overlay.dark};
    ${isOpen
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
        `}
    transition: opacity 0.3s;

    ${safeArea('top')}
    ${safeArea('bottom')}
  `;

export interface OverlayProps {
  open: boolean;
  name?: string;
  depth?: number;
  blocking?: boolean;
  onClose: OverlayState['close'];
  onClosePageClick?: OverlayState['close'];
}

const Overlay = ({
  className,
  open,
  name,
  depth,
  blocking = true,
  onClose,
  onClosePageClick,
  children,
}: PropsWithChildren<OverlayProps & CSSProps>) => {
  const isMounted = useMounted();

  const [delayOpen, setDelayOpenImmediate, setDelayOpen] =
    useDelayedState(open);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setDelayOpen(true);
    } else {
      setDelayOpenImmediate(false);
    }
  }, [open]);

  return isMounted
    ? createPortal(
        <div
          id={name}
          css={overlayCSS(open, delayOpen, depth)}
          className={className}
          onClick={() => onClosePageClick && onClosePageClick()}
          ref={ref}
        >
          <div
            css={bgCSS(open)}
            onClick={blocking ? () => onClose() : undefined}
          />
          {children}
        </div>,
        document.getElementById('overlay')!
      )
    : null;
};

export default Overlay;

const overlayCloseCSS = css`
  position: absolute;
  top: ${Spacing.xsmall}px;
  right: ${Spacing.xsmall}px;
  z-index: 9;
`;

export const OverlayClose = ({ onClose }: Pick<OverlayProps, 'onClose'>) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <button
      css={overlayCloseCSS}
      className="icon-btn"
      aria-label={t('닫기')}
      onClick={() => onClose()}
    >
      <Icon icon={Close} color={theme.var.black} />
    </button>
  );
};
